/**
 * useOrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord, OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsState =
  | 'active';

export type OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent =
  | 'INIT'
  | 'TRACK'
  | 'REPORTED'
  | 'REPORT_FAILED';

type OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot = 'system';
interface OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsInternalState {
  machineState: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot, OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord>;
}

type OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[]; renderClear?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsBeginnerTaskManagerSLDocAnalyticsReducer(
  state: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsInternalState,
  action: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsAction
): OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsInternalState = {
  machineState: 'active',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  domain: string;
  endpoint: string;
  path?: string;
  statusSlot: string;
}

const DEFAULT_ORB_DOC_TUTORIALS_BEGINNER_TASK_MANAGER_SL_DOC_ANALYTICS_CONFIG = {
  domain: "orb.almadar.io",
  endpoint: "https://almadar-analytics--kflow-b3a39.europe-west4.hosted.app/e",
  statusSlot: "system",
} as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsConfig;

export interface OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsLogicReturn {
  state: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot, OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsConfig;
}

const __LEG_EVENTS: ReadonlySet<string> = new Set(['INIT', 'TRACK']);

export function useOrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsLogic(config?: Partial<OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsConfig>): OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsLogicReturn {
  const mergedConfig: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsConfig = { ...DEFAULT_ORB_DOC_TUTORIALS_BEGINNER_TASK_MANAGER_SL_DOC_ANALYTICS_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-beginner-task-manager-sl-doc-analytics', orbitalName: 'OrbDocTutorialsBeginnerTaskManagerSL', traitName: 'OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsBeginnerTaskManagerSLDocAnalyticsReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'active' && event === 'INIT') {
      // active → active (on INIT)
      _newState = 'active';
    } else if (fromState === 'active' && event === 'TRACK') {
      // active → active (on TRACK)
      _newState = 'active';
    } else if (fromState === 'active' && event === 'REPORTED') {
      // active → active (on REPORTED)
        _fields['status'] = (payloadObj?.sent ? "reported" : "skipped");
        fields = { ...(fields ?? {}), 'status': _fields['status'] } as Record<string, EventPayloadValue | undefined>;
        _fields['reported'] = (payloadObj?.sent ? (+(fields?.reported ?? 0) + 1) : fields?.reported);
        fields = { ...(fields ?? {}), 'reported': _fields['reported'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'active';
    } else if (fromState === 'active' && event === 'REPORT_FAILED') {
      // active → active (on REPORT_FAILED)
        _fields['status'] = "failed";
        fields = { ...(fields ?? {}), 'status': _fields['status'] } as Record<string, EventPayloadValue | undefined>;
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'active';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[]; clear: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[] } => {
    switch (fromState) {
      case 'active':
        switch (event) {
          case 'INIT': return { write: ['system'] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[] };
          case 'REPORTED': return { write: ['system'] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[] };
          case 'REPORT_FAILED': return { write: ['system'] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsBeginnerTaskManagerSLAnalyticsRecord': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'TRACK': (payload?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap['TRACK']) => dispatch('TRACK', payload),
    'REPORTED': (payload?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap['REPORTED']) => dispatch('REPORTED', payload),
    'REPORT_FAILED': (payload?: OrbDocTutorialsBeginnerTaskManagerSLDocAnalyticsEventPayloadMap['REPORT_FAILED']) => dispatch('REPORT_FAILED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsBeginnerTaskManagerSL.OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics', ['INIT', 'TRACK', 'REPORTED', 'REPORT_FAILED'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsBeginnerTaskManagerSLDocAnalytics',
      currentState: internalStateRef.current.machineState,
      states: ['active'],
      events: ['INIT', 'TRACK', 'REPORTED', 'REPORT_FAILED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.path = mergedConfig.path ?? (["/sl", "/docs/tutorials/beginner/task-manager"].join(''));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
